import { createContext } from 'preact';
import type { ComponentChildren } from 'preact';
import { useContext, useMemo } from 'preact/hooks';

/**
 * ScopeContext provides team-scoped context for the application
 * Team-only architecture - all routes require team context
 */
export interface ScopeContext {
  scope: 'team'; // Team-only architecture
  teamId: string; // Always required in team-only architecture
  userId: string;

  // Helper methods for API calls
  getApiPrefix: () => string;
  getNavigationPrefix: () => string;
}

const ScopeContextInstance = createContext<ScopeContext | null>(null);

interface ScopeProviderProps {
  scope: 'team'; // Team-only architecture
  teamId: string; // Always required
  userId: string;
  children: ComponentChildren;
}

/**
 * ScopeProvider wraps team-scoped routes
 * Provides team context to all child components
 */
export function ScopeProvider({ scope, teamId, userId, children }: ScopeProviderProps) {
  const contextValue = useMemo<ScopeContext>(() => ({
    scope,
    teamId,
    userId,

    getApiPrefix: () => {
      // Team-only architecture - always use team prefix
      return `/teams/${teamId}`;
    },

    getNavigationPrefix: () => {
      // Team-only architecture - always use team prefix
      return `/teams/${teamId}`;
    }
  }), [scope, teamId, userId]);

  return (
    <ScopeContextInstance.Provider value={contextValue}>
      {children}
    </ScopeContextInstance.Provider>
  );
}

/**
 * Hook to access current scope context
 * Must be used within a ScopeProvider
 */
export function useScopeContext(): ScopeContext {
  const context = useContext(ScopeContextInstance);
  if (!context) {
    throw new Error('useScopeContext must be used within a ScopeProvider');
  }
  return context;
}

/**
 * Helper function to create scope context from URL params (team-only architecture)
 * Extracts teamId from URL and creates ScopeContext
 */
export function createScopeFromRoute(path: string, userId: string): ScopeContext {
  const isNewTeamRoute = path.startsWith('/teams/');
  const isLegacyTeamRoute = path.startsWith('/t/');

  if (isNewTeamRoute) {
    // Extract teamId from /teams/:teamId/*
    const teamIdMatch = path.match(/^\/teams\/([^\/]+)/);
    const teamId = teamIdMatch?.[1];

    if (!teamId) {
      throw new Error('Team ID not found in team route');
    }

    return {
      scope: 'team',
      teamId,
      userId,
      getApiPrefix: () => `/teams/${teamId}`,
      getNavigationPrefix: () => `/teams/${teamId}`
    };
  } else if (isLegacyTeamRoute) {
    // Extract teamId from /t/:teamId/* (legacy route)
    const teamIdMatch = path.match(/^\/t\/([^\/]+)/);
    const teamId = teamIdMatch?.[1];

    if (!teamId) {
      throw new Error('Team ID not found in legacy team route');
    }

    return {
      scope: 'team',
      teamId,
      userId,
      getApiPrefix: () => `/teams/${teamId}`, // Still use new API prefix
      getNavigationPrefix: () => `/teams/${teamId}` // Still use new navigation prefix
    };
  } else {
    // Fallback to a default team context - everyone gets a team in team-only architecture
    throw new Error('Team-only architecture requires team context');
  }
}
